import { Link } from 'react-router'
import {
  Shield, CheckCircle, RefreshCcw, HeadphonesIcon,
  BadgeCheck, Lock, Star, ArrowRight,
} from 'lucide-react'

// ─── Guarantee Badge (inline, small) ─────────────────────────

export function GuaranteeBadge({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ${className}`}>
      <Shield className="h-3.5 w-3.5" />
      Garantie AfficheServices
    </span>
  )
}

// ─── Trust Bar (compact, horizontal) ─────────────────────────

export function TrustBar({ className = '' }: { className?: string }) {
  return (
    <div className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-xl bg-slate-50 px-4 py-3 ${className}`}>
      {[
        { icon: BadgeCheck, label: 'Artisans vérifiés' },
        { icon: Shield, label: 'Garantie satisfaction' },
        { icon: Lock, label: 'Paiement sécurisé' },
        { icon: HeadphonesIcon, label: 'Support 7j/7' },
      ].map((item) => (
        <span key={item.label} className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <item.icon className="h-3.5 w-3.5 text-emerald-500" />
          {item.label}
        </span>
      ))}
    </div>
  )
}

// ─── Guarantee Section (full, for homepage/landing) ──────────

export function GuaranteeSection() {
  return (
    <section className="bg-white py-14">
      <div className="mx-auto max-w-7xl px-4 lg:px-6">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100">
            <Shield className="h-7 w-7 text-emerald-600" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-slate-900 sm:text-3xl">
            La Garantie AfficheServices
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-500">
            Nous nous engageons pour votre satisfaction à chaque étape de votre projet.
            Si quelque chose ne va pas, nous intervenons.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: BadgeCheck,
              color: 'bg-blue-50 text-blue-600',
              title: 'Artisans 100% vérifiés',
              desc: 'SIRET valide, assurance RC Pro, identité contrôlée. Chaque professionnel est vérifié avant d\'apparaître sur la plateforme.',
            },
            {
              icon: RefreshCcw,
              color: 'bg-emerald-50 text-emerald-600',
              title: 'Satisfaction ou remédiation',
              desc: 'Si le travail ne correspond pas au devis accepté, nous intervenons pour trouver une solution amiable avec le professionnel.',
            },
            {
              icon: HeadphonesIcon,
              color: 'bg-violet-50 text-violet-600',
              title: 'Support dédié',
              desc: 'Notre équipe est disponible par email et téléphone pour vous accompagner avant, pendant et après vos travaux.',
            },
            {
              icon: Lock,
              color: 'bg-amber-50 text-amber-600',
              title: 'Données protégées',
              desc: 'Vos informations personnelles sont chiffrées et jamais partagées sans votre accord. Conformité RGPD complète.',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${item.color}`}>
                <item.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-sm font-semibold text-slate-900">{item.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Verification process */}
        <div className="mt-10 rounded-2xl bg-slate-50 p-6 sm:p-8">
          <h3 className="text-center text-lg font-semibold text-slate-900">
            Notre processus de vérification
          </h3>
          <div className="mt-6 grid gap-4 sm:grid-cols-5">
            {[
              { step: '1', label: 'Inscription', desc: 'Le pro crée son compte et fournit ses documents' },
              { step: '2', label: 'SIRET', desc: 'Vérification automatique auprès de l\'INSEE' },
              { step: '3', label: 'Assurance', desc: 'Contrôle de l\'attestation RC Pro en cours de validité' },
              { step: '4', label: 'Identité', desc: 'Vérification d\'identité du gérant' },
              { step: '5', label: 'Activation', desc: 'Profil activé, suivi continu de la qualité' },
            ].map((s, _i) => (
              <div key={s.step} className="text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                  {s.step}
                </div>
                <p className="mt-2 text-xs font-semibold text-slate-900">{s.label}</p>
                <p className="mt-1 text-[11px] leading-snug text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to="/demande-devis/1"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
          >
            Demander un devis garanti <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  )
}

// ─── Partner Logos / Social Proof ─────────────────────────────

export function PartnerLogos() {
  const partners = [
    { name: 'FFB', full: 'Fédération Française du Bâtiment', color: 'text-blue-700 bg-blue-50 border-blue-100' },
    { name: 'CAPEB', full: 'Artisans du Bâtiment', color: 'text-red-700 bg-red-50 border-red-100' },
    { name: 'Qualibat', full: 'Certification Qualité', color: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
    { name: 'RGE', full: 'Reconnu Garant Environnement', color: 'text-green-700 bg-green-50 border-green-100' },
    { name: 'DGCCRF', full: 'Protection des Consommateurs', color: 'text-violet-700 bg-violet-50 border-violet-100' },
  ]

  return (
    <section className="border-y border-border bg-white py-8">
      <div className="mx-auto max-w-7xl px-4 lg:px-6">
        <p className="text-center text-xs font-medium uppercase tracking-wider text-slate-500">
          Certifications et normes respectées
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
          {partners.map((p) => (
            <div
              key={p.name}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 ${p.color}`}
              title={p.full}
            >
              <span className="text-sm font-bold">{p.name}</span>
              <span className="hidden text-[10px] font-medium opacity-70 sm:inline">{p.full}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Stats Social Proof (reusable) ───────────────────────────

export function SocialProofStats({ className = '' }: { className?: string }) {
  return (
    <div className={`grid grid-cols-2 gap-4 sm:grid-cols-4 ${className}`}>
      {[
        { value: '100%', label: 'Artisans vérifiés', icon: BadgeCheck, color: 'text-blue-600' },
        { value: '48h', label: 'Délai de réponse', icon: Star, color: 'text-amber-500' },
        { value: 'Gratuit', label: 'Service sans engagement', icon: CheckCircle, color: 'text-emerald-600' },
        { value: '7j/7', label: 'Support disponible', icon: HeadphonesIcon, color: 'text-violet-600' },
      ].map((stat) => (
        <div key={stat.label} className="text-center">
          <stat.icon className={`mx-auto h-5 w-5 ${stat.color}`} />
          <p className="mt-1 text-2xl font-bold text-slate-900">{stat.value}</p>
          <p className="mt-0.5 text-xs text-slate-500">{stat.label}</p>
        </div>
      ))}
    </div>
  )
}
